function extractTechnicalMessage(body: string) {
  try {
    const parsed = JSON.parse(body)
    return String(
      parsed.detail ||
      parsed.message ||
      parsed.error ||
      parsed.title ||
      body
    )
  } catch {
    return body
  }
}

export type ApiFieldError = {
  field: string
  message: string
}

function parseErrorBody(body: string) {
  try {
    return JSON.parse(body)
  } catch {
    return null
  }
}

export function formatApiError(
  error: unknown,
  fallback = "The action could not be completed. Please reload the page and try again."
) {
  if (
    error instanceof Error &&
    ("status" in error || "technicalMessage" in error)
  ) {
    return error.message
  }

  const technicalMessage =
    error instanceof Error ? error.message : String(error || "")
  const normalized = technicalMessage.toLowerCase()

  if (
    normalized.includes("token refresh") ||
    normalized.includes("invalid_request") ||
    normalized.includes("unauthorized") ||
    normalized.includes("security checks") ||
    normalized.includes("valid subscription") ||
    normalized.includes("oauth")
  ) {
    return "Your connection to EUIPO has expired. Please reload the page and sign in again."
  }

  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("networkerror") ||
    normalized.includes("network request failed")
  ) {
    return "The server could not be reached. Please check your connection, reload the page and try again."
  }

  if (
    normalized.includes("status 429") ||
    normalized.includes("too many requests") ||
    normalized.includes("rate limit")
  ) {
    return "EUIPO is receiving too many requests. Please wait a moment and try again."
  }

  if (
    normalized.includes("status 413") ||
    normalized.includes("payload too large") ||
    normalized.includes("file too large")
  ) {
    return "The selected file is too large. Please choose a smaller file and try again."
  }

  if (
    normalized.includes("cannot deserialize") ||
    normalized.includes("input validation") ||
    normalized.includes("validation failed") ||
    normalized.includes("status 400")
  ) {
    return "The submitted data could not be processed. Please review the application and try again."
  }

  if (
    normalized.includes("status 404") ||
    normalized.includes("not found")
  ) {
    return "The requested data could not be found. Please reload the page and try again."
  }

  if (
    normalized.includes("status 500") ||
    normalized.includes("status 502") ||
    normalized.includes("status 503") ||
    normalized.includes("status 504")
  ) {
    return "The service is temporarily unavailable. Please reload the page and try again later."
  }

  return fallback
}

function messageForErrorCode(code: unknown, serverMessage?: unknown) {
  const normalizedCode = String(code || "")

  // The EPO data service words its own messages, whatever the status
  if (normalizedCode === "EPO_OPS_ERROR" && typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage
  }

  if (normalizedCode === "PAYMENT_ALREADY_RECEIVED") {
    return "Payment has already been received for this application."
  }

  if (normalizedCode === "EUIPO_SUBMISSION_FAILED_AFTER_PAYMENT") {
    return "The payment was confirmed, but the application could not be submitted. Please contact support."
  }

  if (normalizedCode === "EUIPO_SUBMISSION_FAILED_BEFORE_INVOICE") {
    return "EUIPO could not accept the application. No invoice was sent. Please review the application or contact support."
  }

  if (normalizedCode === "EUIPO_SUBMISSION_ERROR") {
    return "EUIPO returned an error for this application. Please review the application or contact support."
  }

  if (normalizedCode.startsWith("EUIPO_SUBMISSION_FAILED")) {
    return "EUIPO could not accept the application right now. Please try again later."
  }

  if (
    normalizedCode === "UPLOAD_FAILED" ||
    normalizedCode === "UPLOAD_VALIDATION_ERROR"
  ) {
    if (typeof serverMessage === "string" && serverMessage.trim()) {
      return serverMessage
    }

    return "The selected file does not meet the upload requirements. Please check the file type, size and image format."
  }

  if (normalizedCode.startsWith("UPLOAD_")) {
    return "The selected file does not meet the upload requirements. Please check the file type, size and image format."
  }

  return null
}

export function createApiError(status: number, body: string) {
  const parsed = parseErrorBody(body)
  const technicalMessage = extractTechnicalMessage(body)
  const codeMessage = messageForErrorCode(parsed?.code, parsed?.message)
  const shouldUseServerMessage = parsed?.message && parsed.code === "VALIDATION_ERROR"
  const error = new Error(
    codeMessage ||
      (shouldUseServerMessage
        ? parsed.message
        : formatApiError(
            new Error(`status ${status}: ${technicalMessage}`)
          ))
  )

  Object.assign(error, {
    status,
    technicalMessage,
    code: parsed?.code,
    fields: Array.isArray(parsed?.fields) ? parsed.fields : []
  })

  return error
}

export function getApiFieldErrors(error: unknown): ApiFieldError[] {
  if (
    error &&
    typeof error === "object" &&
    "fields" in error &&
    Array.isArray((error as any).fields)
  ) {
    return (error as any).fields.filter((item: any) =>
      typeof item?.field === "string" &&
      typeof item?.message === "string"
    )
  }

  return []
}
